import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { CardList, CardListItem } from '@/shared/ui/CardList';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { PayslipIcon, UploadIcon } from '@/shared/ui/icons';
import { formatDate } from '@/shared/utils/format';

import { BATCH_PAGE } from './api';
import { PayrollTabs } from './PayrollTabs';
import {
  PAYROLL_ACCEPT,
  type PayrollBatch,
  type PayrollStatus,
  payslips,
  periodText,
  stateLabel,
  stateTone,
  unassignedCount,
} from './types';
import { usePayrollBatches, usePayrollStatus, useUploadPayroll } from './usePayroll';

const day = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });
/** A date without a time (`purges_on`): read as the day it says, wherever the browser is. */
const plainDay = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeZone: 'UTC' });

const HELP = [
  <>
    Las nóminas llegan solas de la asesoría, o se suben aquí con <strong>Subir nóminas</strong> tal
    como llegan: el <code>.zip</code> o el PDF.
  </>,
  <>
    La aplicación las separa por persona mirando el <strong>NIF</strong> y el{' '}
    <strong>nombre</strong>, nunca con la IA. Lo que no casa sale <strong>Sin persona</strong> para
    que elijas a quién va.
  </>,
  <>
    Cada persona recibe un correo solo con la suya, en un PDF que se abre con{' '}
    <strong>su contraseña</strong> (la de «Trabajadores»). No se guarda copia en Enviados.
  </>,
  <>
    A los <strong>30 días</strong> se borran los ficheros. Queda a quién se mandó cada una y cuándo.
  </>,
];

/**
 * «Nóminas» (contract v10.53, ADR 0047 of the back): every month that came, the newest
 * first, with how many payslips it brought, how many have a person and how many went out.
 * What did not look like the payroll and what was discarded go folded under the list:
 * they are there in case, not to be read every month.
 */
export function PayrollPage() {
  const navigate = useNavigate();
  const status = usePayrollStatus();
  const [offset, setOffset] = useState(0);
  const batches = usePayrollBatches(offset);
  const upload = useUploadPayroll();
  const input = useRef<HTMLInputElement>(null);

  const items = batches.data?.items ?? [];
  const current = items.filter(
    (batch) => batch.status !== 'not_payroll' && batch.status !== 'discarded',
  );
  const folded = items.filter(
    (batch) => batch.status === 'not_payroll' || batch.status === 'discarded',
  );

  const uploadButton = (
    <>
      <input
        ref={input}
        type="file"
        accept={PAYROLL_ACCEPT}
        hidden
        aria-label="Fichero de las nóminas"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          upload.mutate(file, { onSuccess: (batch) => void navigate(`/nominas/${batch.id}`) });
        }}
      />
      <Button
        icon={<UploadIcon size={18} />}
        disabled={upload.isPending}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? 'Subiendo…' : 'Subir nóminas'}
      </Button>
    </>
  );

  return (
    <>
      <PageHeader title="Nóminas" help={HELP} actions={uploadButton} />
      <PayrollTabs />

      {upload.isError ? (
        <ErrorMessage error={upload.error}>No se pudieron subir las nóminas</ErrorMessage>
      ) : null}

      {status.data ? <StatusNotes status={status.data} /> : null}

      {batches.isPending ? <Spinner variant="block" label="Cargando las nóminas" /> : null}
      {batches.isError ? (
        <ErrorMessage error={batches.error}>No se pudieron cargar las nóminas</ErrorMessage>
      ) : null}

      {batches.data && items.length === 0 ? (
        <EmptyState
          icon={<PayslipIcon size={24} />}
          title="Todavía no ha llegado ninguna"
          hint="Llegan solas de la asesoría cuando las manda, o súbelas con «Subir nóminas»."
        />
      ) : null}

      {current.length > 0 ? <BatchList items={current} label="Nóminas recibidas" /> : null}

      {folded.length > 0 ? (
        <details className="group">
          <summary className="text-ink-2 cursor-pointer text-[14px] font-medium select-none">
            Lo que no parecía nómina y lo descartado ({folded.length})
          </summary>
          <div className="mt-3">
            <BatchList items={folded} label="No parecía nómina o descartado" />
          </div>
        </details>
      ) : null}

      {batches.data && batches.data.total > BATCH_PAGE ? (
        <div className="text-ink-3 flex items-center justify-end gap-3 text-[13px]">
          <span>
            {offset + 1}–{offset + items.length} de {batches.data.total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset((value) => Math.max(0, value - BATCH_PAGE))}
          >
            Más nuevas
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={offset + BATCH_PAGE >= batches.data.total}
            onClick={() => setOffset((value) => value + BATCH_PAGE)}
          >
            Más antiguas
          </Button>
        </div>
      ) : null}
    </>
  );
}

/**
 * What the screen says before the list, only when there is something to say: the sending
 * switched off, a list of employees that is empty or incomplete, where the files come
 * from. Each one says what to do next.
 */
function StatusNotes({ status }: { status: PayrollStatus }) {
  const mailboxes = status.mailboxes ?? [];
  const missing = status.employees_without_email ?? 0;
  const noPassword = status.employees_without_password ?? 0;
  return (
    <div className="flex flex-col gap-3">
      {status.lock_closed ? (
        <Callout tone="warn" title="El envío de correo está desactivado en la plataforma">
          Se pueden preparar y revisar, pero no sale ninguna hasta que quien mantiene la aplicación
          lo active.
        </Callout>
      ) : null}
      {(status.employees ?? 0) === 0 ? (
        <Callout
          tone="info"
          title="Falta la lista de trabajadores"
          action={
            <Link to="/nominas/trabajadores" className="link text-[13.5px]">
              Ir a Trabajadores
            </Link>
          }
        >
          Con ella se sabe de quién es cada nómina y a qué correo va. Se carga una vez.
        </Callout>
      ) : missing > 0 || noPassword > 0 ? (
        <Callout
          tone="warn"
          title={[
            missing > 0
              ? `${missing === 1 ? 'Una persona' : `${missing} personas`} sin correo`
              : '',
            noPassword > 0 ? `${noPassword === 1 ? 'una' : noPassword} sin contraseña` : '',
          ]
            .filter(Boolean)
            .join(' y ')}
          action={
            <Link to="/nominas/trabajadores" className="link text-[13.5px]">
              Ir a Trabajadores
            </Link>
          }
        >
          A quien le falte no se le puede enviar la suya.
        </Callout>
      ) : null}
      <p className="text-ink-3 text-[13.5px]">
        {mailboxes.length === 0 ? (
          <>Ningún buzón las recibe todavía: súbelas con «Subir nóminas».</>
        ) : (
          mailboxes.map((mailbox) => (
            <span key={mailbox.name} className="block">
              {mailbox.sender ? (
                <>
                  Llegan solas de <strong className="text-ink-2">{mailbox.sender}</strong> a{' '}
                  {mailbox.address}.
                </>
              ) : (
                <>
                  {mailbox.address} no se lee: falta la dirección de la asesoría. Quien mantiene la
                  aplicación la pone.
                </>
              )}
              {mailbox.last_error ? (
                <span className="text-warn">
                  {' '}
                  La última vez no se pudo leer; si no llegan, súbelas a mano.
                </span>
              ) : null}
            </span>
          ))
        )}{' '}
        Los ficheros se borran a los {status.keep_days ?? 30} días.
      </p>
    </div>
  );
}

function arrived(batch: PayrollBatch): string {
  const when = formatDate(day, batch.received_at);
  if (batch.source === 'upload') {
    return batch.created_by ? `${when} · subidas por ${batch.created_by}` : `${when} · subidas`;
  }
  const who = batch.sender_name || batch.sender;
  return who ? `${when} · de ${who}` : when;
}

function counts(batch: PayrollBatch): string {
  if (!(batch.slips ?? 0)) return '';
  const parts = [payslips(batch.slips ?? 0)];
  const unassigned = unassignedCount(batch);
  if (batch.status === 'ready' && unassigned > 0) parts.push(`${unassigned} sin persona`);
  if ((batch.sent ?? 0) > 0) parts.push(`${batch.sent ?? 0} enviadas`);
  return parts.join(' · ');
}

function deletion(batch: PayrollBatch): string {
  if (batch.purged_at) return 'Ya borradas';
  return batch.purges_on ? formatDate(plainDay, batch.purges_on) : '';
}

function BatchList({ items, label }: { items: PayrollBatch[]; label: string }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="hidden md:block">
        <Card padded={false}>
          <Table label={label} minWidth="0">
            <THead>
              <tr>
                <Th className="w-44">Mes</Th>
                <Th>Llegaron</Th>
                <Th>Nóminas</Th>
                <Th className="w-48">Estado</Th>
                <Th className="w-32">Se borran</Th>
              </tr>
            </THead>
            <TBody>
              {items.map((batch) => (
                <Tr
                  key={batch.id}
                  interactive
                  onClick={() => void navigate(`/nominas/${batch.id}`)}
                >
                  <Td className="text-ink font-medium">{periodText(batch.period) ?? 'Sin mes'}</Td>
                  <Td className="text-ink-2">{arrived(batch)}</Td>
                  <Td className="text-ink-2 tabular-nums">{counts(batch)}</Td>
                  <Td>
                    <Badge tone={stateTone(batch)}>{stateLabel(batch)}</Badge>
                  </Td>
                  <Td className="text-ink-3 tabular-nums">{deletion(batch)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        </Card>
      </div>
      <CardList label={label} className="md:hidden">
        {items.map((batch) => (
          <CardListItem
            key={batch.id}
            to={`/nominas/${batch.id}`}
            title={periodText(batch.period) ?? 'Sin mes'}
            subtitle={arrived(batch)}
            aside={<Badge tone={stateTone(batch)}>{stateLabel(batch)}</Badge>}
            footer={
              <span>
                {[counts(batch), deletion(batch) ? `se borran: ${deletion(batch)}` : '']
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            }
          />
        ))}
      </CardList>
    </>
  );
}
